import { FileText, Loader2, X } from 'lucide-react'
import { isFileable, NOT_FILEABLE_TITLE } from './fileTypes'
import type { SparkyAttachment } from './types'

/** A chip in the composer: a file that is uploading, pending, or refused. */
export interface ComposerChip {
  key: string
  filename: string
  status: 'uploading' | 'ready' | 'error'
  attachment?: SparkyAttachment
  error?: string
}

/** Pending files above the composer; each can be removed (or dismissed when refused). */
export function ComposerChips({
  chips,
  onRemove,
}: {
  chips: readonly ComposerChip[]
  onRemove: (chip: ComposerChip) => void
}) {
  if (chips.length === 0) return null
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label="Bijlagen">
      {chips.map((chip) => (
        <li
          key={chip.key}
          className="flex max-w-full items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2 py-1 text-xs"
        >
          {chip.status === 'uploading' ? (
            <Loader2 className="size-3.5 shrink-0 animate-spin" aria-label="Bezig met uploaden" />
          ) : (
            <FileText className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          )}
          <span className="flex min-w-0 flex-col">
            <span className="truncate">{chip.filename}</span>
            {chip.status === 'error' ? (
              <span role="alert" className="text-destructive">
                {chip.error}
              </span>
            ) : chip.attachment?.input_kind === 'pdf' && chip.attachment.send_as === 'text' ? (
              <span className="text-muted-foreground">als tekst</span>
            ) : null}
          </span>
          {chip.status === 'uploading' ? null : (
            <button
              type="button"
              aria-label={`${chip.filename} verwijderen`}
              className="rounded p-0.5 text-muted-foreground hover:text-foreground"
              onClick={() => onRemove(chip)}
            >
              <X className="size-3.5" aria-hidden />
            </button>
          )}
        </li>
      ))}
    </ul>
  )
}

/** Files of a sent message, with the "Opslaan bij deal" action. */
export function SentAttachments({
  attachments,
  savedNames,
  onSave,
}: {
  attachments: readonly SparkyAttachment[]
  /** Startup names of attachments saved in this session (the API also reports it on reload). */
  savedNames: Readonly<Record<string, string>>
  onSave: (attachment: SparkyAttachment) => void
}) {
  if (attachments.length === 0) return null
  return (
    <ul className="mt-2 flex flex-col gap-1">
      {attachments.map((attachment) => {
        const savedName =
          savedNames[attachment.id] ??
          (attachment.saved_document_id ? (attachment.saved_deal_name ?? 'deal') : null)
        const fileable = isFileable(attachment)
        return (
          <li
            key={attachment.id}
            className="flex flex-wrap items-center gap-2 rounded-md bg-background/20 px-2 py-1 text-xs"
          >
            <FileText className="size-3.5 shrink-0" aria-hidden />
            <span className="min-w-0 truncate">{attachment.filename}</span>
            {savedName ? (
              <span>Opgeslagen bij {savedName}</span>
            ) : (
              <button
                type="button"
                className="underline underline-offset-2 disabled:no-underline disabled:opacity-60"
                disabled={!fileable}
                title={fileable ? undefined : NOT_FILEABLE_TITLE}
                onClick={() => onSave(attachment)}
              >
                Opslaan bij deal
              </button>
            )}
          </li>
        )
      })}
    </ul>
  )
}
